import { useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Card,
  Divider,
  Group,
  MultiSelect,
  Select,
  Text,
  TextInput,
  Textarea,
} from '@mantine/core'
import { useForm } from '@mantine/form'
import { IconCircleCheck, IconSend } from '@tabler/icons-react'
import { DatePickerInput } from '@mantine/dates'
import { formatDate, REQUEST_RECIPIENT } from './intake'
import {
  buildUserAccessOutlookUrl,
  USER_ACCESS_INITIAL,
  type UserAccessValues,
} from './userAccess'

const required = (message: string) => (v: string | string[]) => {
  if (Array.isArray(v)) return v.length === 0 ? message : null
  return String(v ?? '').trim().length === 0 ? message : null
}

export function UserAccessForm() {
  const [submitted, setSubmitted] = useState(false)

  const form = useForm<UserAccessValues>({
    initialValues: USER_ACCESS_INITIAL,
    validate: {
      requestType: required('Request Type is required'),
      requestor: required('Requestor is required'),
      requestedUsers: required('Requested User(s) is required'),
      platform: required('At least one platform is required'),
      accessLevel: required('Access Level is required'),
      businessJustification: required('Business Justification is required'),
      startDate: required('Start Date is required'),
      approvalObtained: required('Approval Obtained is required'),
    },
  })

  const handleSubmit = form.onSubmit((values) => {
    const loginHint = localStorage.getItem('prism_username')
    window.open(
      buildUserAccessOutlookUrl(values, loginHint),
      '_blank',
      'noopener,noreferrer'
    )
    setSubmitted(true)
  })

  return (
    <Box>
      <Text size="sm" c="gray.6" mb="md">
        Request new, modified, or removed access to PRISM, Databricks, Genie,
        Data Catalog, Data Lineage, or other data platform tools. Submitting
        opens an Outlook email to {REQUEST_RECIPIENT}.
      </Text>

      {submitted && (
        <Alert
          color="green"
          icon={<IconCircleCheck size={16} />}
          title="Email drafted in Outlook"
          mb="md"
          withCloseButton
          onClose={() => setSubmitted(false)}
        >
          A &quot;Prism Portal Request - User Access&quot; email to{' '}
          {REQUEST_RECIPIENT} should have opened in an Outlook compose tab.
          Review and send it to complete your request.
        </Alert>
      )}

      <Card shadow="sm" withBorder>
        <Box p="md">
          <form onSubmit={handleSubmit}>
            <Group grow mb="md">
              <Select
                label="Request Type (Required)"
                placeholder="Select"
                data={['New Access', 'Modify Access', 'Remove Access']}
                {...form.getInputProps('requestType')}
              />
              <TextInput
                label="Requestor (Required)"
                placeholder="Your name"
                {...form.getInputProps('requestor')}
              />
            </Group>

            <TextInput
              label="Requested User(s) (Required)"
              placeholder="Comma-separated names or emails"
              mb="md"
              {...form.getInputProps('requestedUsers')}
            />

            <Group grow mb="md">
              <MultiSelect
                label="Platform (Required)"
                placeholder="Select one or more"
                data={[
                  'Databricks',
                  'Databricks Genie',
                  'Data Catalog',
                  'Data Lineage',
                ]}
                {...form.getInputProps('platform')}
              />
              <Select
                label="Access Level Needed (Required)"
                placeholder="Select"
                data={['Read Only (Default)', 'Read Write']}
                {...form.getInputProps('accessLevel')}
              />
            </Group>

            <TextInput
              label="Similar Access As (Name of existing user)"
              placeholder="e.g. Jane Doe"
              mb="md"
              {...form.getInputProps('similarAccessAs')}
            />

            <Textarea
              label="Business Justification (Required)"
              placeholder="Why is this access needed?"
              minRows={2}
              autosize
              mb="md"
              {...form.getInputProps('businessJustification')}
            />

            <Group grow mb="md">
              <DatePickerInput
                label="Start Date (Required)"
                placeholder="Pick a date"
                valueFormat="M/D/YYYY"
                value={form.values.startDate ? new Date(form.values.startDate) : new Date()}
                onChange={(d) => form.setFieldValue('startDate', d ? formatDate(d) : '')}
                error={form.errors.startDate}
              />
              <TextInput
                label="Approval Obtained (Required)"
                placeholder="Manager, Data Owner"
                {...form.getInputProps('approvalObtained')}
              />
            </Group>

            <Divider my="md" />
            <Group justify="flex-end">
              <Button
                variant="light"
                color="gray"
                onClick={() => {
                  form.reset()
                  setSubmitted(false)
                }}
              >
                Clear
              </Button>
              <Button
                type="submit"
                color="prism"
                leftSection={<IconSend size={16} />}
              >
                Submit Request
              </Button>
            </Group>
          </form>
        </Box>
      </Card>
    </Box>
  )
}
